<script setup lang="ts">
import type { AppRatingStatsOut } from '~/types/product.model'
import RatingStar from '~/components/product/review/RatingStar.vue'
import RatingStars from '~/components/product/review/RatingStars.vue'

const props = withDefaults(defineProps<{
  ratingStats: AppRatingStatsOut | null | undefined
  size?: 'small' | 'medium' | 'large'
  isSingleStar?: boolean
  showWhenNoRatings?: boolean
  showNoRatingsMessage?: boolean
}>(), {
  size: 'small',
  isSingleStar: false,
  showWhenNoRatings: false,
  showNoRatingsMessage: false,
})

const shouldShowRating = computed(() => {
  return props.ratingStats?.average_rating || props.showWhenNoRatings
})
</script>

<template>
  <div
    v-if="shouldShowRating"
    class="rating-minimal"
  >
    <div
      class="rating-minimal__container"
      :class="`--${size}`"
    >
      <span v-if="ratingStats?.average_rating">
        <span class="sr-only">App is rated on average</span>
        {{ ratingStats.average_rating.toFixed(1) }}
      </span>
      <span v-else>
        n/a
      </span>
      <RatingStar
        v-if="isSingleStar"
        :percentage-filled="ratingStats?.average_rating ? 100 : 0"
        :size="size"
      />
      <RatingStars
        v-else
        :rating="ratingStats?.average_rating || 0"
        :size="size"
      />
      <span
        v-if="ratingStats?.average_rating"
        class="rating-minimal__number-of-ratings"
      >
        (<span class="sr-only">based on</span>{{ ratingStats.number_of_ratings }}<span class="sr-only">{{ ratingStats.number_of_ratings === 1 ? 'rating' : 'ratings' }}</span>)
      </span>
    </div>
    <p
      v-if="!ratingStats?.average_rating && showNoRatingsMessage"
      class="rating-minimal__no-ratings-message"
    >
      This app has not received any ratings yet.
    </p>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.rating-minimal__container {
  display: flex;
  gap: var(--app-spacer-4);
  align-items: center;

  &.--medium {
    @extend %font-20-30-regular;
  }
}

.rating-minimal__number-of-ratings {
  display: inline-flex;
}

.rating-minimal__no-ratings-message {
  margin-top: var(--app-spacer-4);
}
</style>
